import { CSSProperties, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Banner from 'elements/Banner';
import ButtonSymphony from 'elements/ButtonSymphony';
import Icon from 'elements/Icon';
import Colors from 'modules/Colors';
import useOrganizationV2 from 'Hooks/useOrganizationV2';
import usePlans from 'Hooks/usePlans';
import useAppView from 'Hooks/useAppView';
import { PlanType } from 'modules/Const';
import { Body2 } from './Typography';
import { styx } from 'modules/Utils';

interface TeamTrialBannerProps {
    className?: string;
    style?: CSSProperties;
}

const TeamTrialBanner = ({
    className = 'teamTrialBanner',
    style
}: TeamTrialBannerProps) => {
    const location = useLocation();
    const { organization, hasOrganizationAdminAccess, getIsTrialActionable, openPaymentModal } = useOrganizationV2();
    const { getMonthlyPrice } = usePlans();
    const { isTeamView } = useAppView();

    // Define routes that should show the team trial banner
    const teamRoutesThatShowBanner = [
        '/marketing',
        '/website',
        '/team/dashboard',
        '/team/connections',
        '/team/users',
        '/' // team dashboard redirect
    ];

    // Check if current route should show the banner
    const shouldShowOnCurrentRoute = teamRoutesThatShowBanner.some(route => {
        // Exact match for most routes, but handle root path specially
        if (route === '/') {
            return location.pathname === '/' && isTeamView()
        }
        return location.pathname === route
    });

    // Don't show banner if not on allowed route
    if (!shouldShowOnCurrentRoute) {
        return null;
    }

    // Don't show banner if no organization or trial has ended
    if (!organization?.trial_end_date) {
        return null;
    }

    // Only show to organization admins - view-only users can't add payment methods
    if (!hasOrganizationAdminAccess()) {
        return null;
    }

    // Don't show banner if trial is not actionable (cancelled, etc.)
    if (!getIsTrialActionable()) {
        return null;
    }

    const trialEndDate = new Date(organization.trial_end_date);
    const now = new Date();
    const daysRemaining = Math.max(0, Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
    const hasTrialExpired = daysRemaining <= 0;

    // Don't show if trial has been active for a while (assume payment method added)
    if (!hasTrialExpired && daysRemaining > 7) {
        return null;
    }

    const handleAddPaymentMethod = () => {
        openPaymentModal();
    };

    const renderTrialBanner = () => {
        if (hasTrialExpired) {
            return (
                <Banner
                    className={className}
                    color="red"
                    style={styx(style, {
                        maxWidth: 1200
                    })}
                    iconLeft={<Icon className='warning' icon="warning" size={24} color={Colors.red} />}
                    title="Your team trial has ended!"
                >
                    <div style={sty.bannerContent}>
                        <Body2 style={sty.description}>
                            Please add a payment method to continue using your team. You currently have{' '}
                            {organization?.brands?.length || 0} brands on your team.
                        </Body2>
                        
                        <div style={sty.actionButtons}>
                            <ButtonSymphony
                                className="addPaymentMethodButton"
                                onClick={handleAddPaymentMethod}
                                variant="primary"
                                color="red"
                                style={sty.primaryButton}
                            >
                                Add Payment Method
                            </ButtonSymphony>
                        </div>
                    </div>
                </Banner>
            );
        }

        return (
            <Banner
                className={className}
                color="yellow"
                style={styx(style, {
                    maxWidth: 1200
                })}
                iconLeft={<Icon className='clock' icon="clock" size={24} color={Colors.yellow} />}
                title={`You have ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left on your team trial`}
            >
                <div style={sty.bannerContent}>
                    <Body2 style={sty.description}>
                        Add a payment method now to ensure uninterrupted access to your team features.
                    </Body2>
                    
                    <div style={sty.actionButtons}>
                        <ButtonSymphony
                            className="addPaymentMethodButton"
                            onClick={handleAddPaymentMethod}
                            variant="primary"
                            color="purple"
                            style={sty.primaryButton}
                        >
                            Add Payment Method
                        </ButtonSymphony>
                    </div>
                </div>
            </Banner>
        );
    };

    return renderTrialBanner();
};

const sty: Record<string, CSSProperties> = {
    bannerContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        maxWidth: 1200,
    },
    description: {
        color: Colors.greyDark,
        lineHeight: '1.4'
    },
    actionButtons: {
        display: 'flex',
        gap: 12,
        alignItems: 'center'
    },
    primaryButton: {
        minWidth: 180
    }
};

export default TeamTrialBanner;
